import { valueTypeOptions } from '@unistocks/features/dashboard/utils';
import {
  CalculatedValueResultDto,
  CurrencyDto,
  KeyFinancialRatio,
  NetSavingsType,
  ValueSource,
  ValueType
} from '@unistocks/proxy/axios-client';
import { hasFlag, isNumber } from '@unistocks/shared/utils/util';

/**
 * Single writer of the CalculatedValue wire contract for previews and tests.
 * Keeps the stringly-typed extraProperties keys (ValueType[flag], NetSavingsType,
 * KeyFinancialRatio, Market) in one place.
 */

const currency: CurrencyDto = { displayName: 'Svenska kronor', code: 'SEK' };

export const exampleCalculatedValueMap = new Map<ValueType, CalculatedValueResultDto>([
  [
    ValueType.TotalValue,
    {
      displayName: '',
      value: 10000,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.TotalValue,
      currency: currency,
      isStale: false,
      extraProperties: {
        [ValueType[ValueType.TotalValue]]: 10000
      }
    }
  ],
  [
    ValueType.NominalChange,
    {
      displayName: 'Avkastning',
      value: 500,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.NominalChange,
      currency: currency,
      isStale: false,
      extraProperties: {
        [ValueType[ValueType.NominalChange]]: 500
      }
    }
  ],
  [
    ValueType.PercentageChange,
    {
      displayName: 'Upp/ned',
      value: 0.05,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.PercentageChange,
      currency: null,
      isStale: false,
      extraProperties: {
        [ValueType[ValueType.PercentageChange]]: 0.05
      }
    }
  ],
  [
    ValueType.NetSavings,
    {
      displayName: 'Sparande',
      value: 2500,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.NetSavings,
      currency: currency,
      isStale: false,
      extraProperties: {
        NetSavingsType: NetSavingsType.PerMonth
      }
    }
  ],
  [
    ValueType.KeyFinancialRatio,
    {
      displayName: 'CAGR',
      value: 0.1,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.KeyFinancialRatio,
      currency: null,
      isStale: false,
      extraProperties: {
        KeyFinancialRatio: KeyFinancialRatio.CAGR
      }
    }
  ],
  [
    ValueType.RelativeValue,
    {
      displayName: 'Andel kontanta medel',
      value: 0.08,
      valueSource: ValueSource.Portfolio,
      valueType: ValueType.RelativeValue,
      currency: null,
      isStale: false,
      extraProperties: {}
    }
  ]
]);

/**
 * Build a single example result for the current value type selection.
 * Combines the per-type examples into one row so that multiple selected
 * value types render as multiple values in a single row using extraProperties.
 */
export const getCombinedExampleValue = (
  valueSource: ValueSource,
  valueType: ValueType,
  displayName?: string,
  netSavingsType?: NetSavingsType
): CalculatedValueResultDto => {
  const selected = valueTypeOptions.filter(({ value }) => hasFlag(valueType, value));
  const examples = selected.map(({ value }) => exampleCalculatedValueMap.get(value)!);

  const base = examples[0];
  const extraProperties = examples.reduce<Record<string, any>>(
    (result, example) => ({ ...result, ...example.extraProperties }),
    {}
  );

  if (isNumber(netSavingsType)) extraProperties['NetSavingsType'] = netSavingsType;

  return {
    ...base,
    valueSource,
    valueType,
    displayName: displayName || base.displayName,
    extraProperties
  };
};

/**
 * Example asset list rows used by the asset list layout preview.
 */
export const createAssetListExamples = (valueType: ValueType): CalculatedValueResultDto[] =>
  [
    {
      displayName: 'Example asset',
      value: 0.0465,
      valueSource: ValueSource.Asset,
      valueType,
      currency: { displayName: 'SEK', code: 'SEK' },
      isStale: false,
      extraProperties: {
        Market: 'se_market',
        [ValueType[ValueType.TotalValue]]: 74.5,
        [ValueType[ValueType.NominalChange]]: 5000,
        [ValueType[ValueType.PercentageChange]]: 0.0465
      }
    },
    {
      displayName: 'Example asset',
      value: -0.0322,
      valueSource: ValueSource.Asset,
      valueType,
      currency: { displayName: 'SEK', code: 'SEK' },
      isStale: false,
      extraProperties: {
        Market: 'se_market',
        [ValueType[ValueType.TotalValue]]: 157000,
        [ValueType[ValueType.NominalChange]]: -3500,
        [ValueType[ValueType.PercentageChange]]: -0.0322
      }
    }
  ] as const;
